/* eslint-disable camelcase */
import axios from "axios";
import axiosAPIInstance, { getAxiosInstance } from "./api";
import { client } from "axios-oauth-client";
import { tokenUrl, clientId, idpUrl } from "./config";
import jwtDecode from "jwt-decode";
import { User } from "data-models/user";
import {
  GoogleAuthProvider,
  SAMLAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
} from "firebase/auth";
import { auth } from "./firebase";
import { cookies } from "./cookies";

//const provider = new GoogleAuthProvider()
const provider = new SAMLAuthProvider("saml.avesta-idp");

const isTest = window.location.hostname.includes("test");
const token_key = isTest ? "test_token" : "token";
const refresh_token_key = isTest ? "test_refresh_token" : "refresh_token";
const user_key = isTest ? "test_user" : "user";

const AuthService = {
  getProfile: () => axiosAPIInstance.get(`/profile`).then(r => r.data),

  login: ({ username, password }: { username: string; password: string }) => {
    return signInWithEmailAndPassword(auth, username, password)
      .then(async result => {
        const token = result?.user ? await result.user.getIdToken() : undefined;

        if (!token) {
          throw new Error("No token returned.");
        }

        if (!AuthService.isAppUser(token)) {
          throw new Error("User is not a Myran user.");
        }

        const tokens = {
          access_token: token,
          refresh_token: result.user.refreshToken,
        };
        // eslint-disable-next-line camelcase
        return tokens;
      })
      .catch(err => {
        throw err;
      });
  },

  loginWithCustomProvider: () =>
    signInWithPopup(auth, provider)
      .then(async result => {
        const token = await result?.user.getIdToken();

        if (!token) {
          console.log("No token returned.");
          throw new Error("No token returned.");
        }

        if (!AuthService.isAppUser(token)) {
          console.log("User is not a Myran user.");
          throw new Error("User is not a Myran user.");
        }

        const tokens = {
          access_token: token,
          refresh_token: result.user.refreshToken,
        };
        // eslint-disable-next-line camelcase
        return tokens;
      })
      .catch(err => {
        throw err;
      }),

  loginWithSkolFed: (entityId: string) => {
    const currentBaseUrl = window.location.origin;
    const callbackUrl = `${currentBaseUrl}/callback`;
    const entityIdCode = encodeURI(entityId);
    window.location.href = `${idpUrl}/auth/login?returnUrl=${callbackUrl}&entityId=${entityIdCode}`;
  },

  registerWithSkolFed: (inviteToken: string, entityId: string) => {
    const currentBaseUrl = window.location.origin;
    const callbackUrl = `${currentBaseUrl}/callback`;
    const entityIdCode = encodeURI(entityId);
    window.location.href = `${idpUrl}/auth/login?returnUrl=${callbackUrl}&entityId=${entityIdCode}&invite=${inviteToken}`;
  },

  registerUsingCustomProvider: () =>
    signInWithPopup(auth, provider)
      .then(async result => {
        const token = await result?.user.getIdToken();

        const tokenInfo = jwtDecode(token || "") as any;
        const { name, email } = tokenInfo;

        if (!token) {
          console.log("No token returned.");
          throw new Error("No token returned.");
        }

        // eslint-disable-next-line camelcase
        return {
          name,
          email,
        };
      })
      .catch(err => {
        throw err;
      }),

  logout: () => {
    cookies.remove(token_key);
    cookies.remove(refresh_token_key);
    cookies.remove(user_key);
    window.location.assign(`/login`);
  },

  loginWithCustomToken: ({ customToken }: { customToken: string }) => {
    return signInWithCustomToken(auth, customToken)
      .then(async result => {
        const token = result?.user ? await result.user.getIdToken() : undefined;

        if (!token) {
          throw new Error("No token returned.");
        }

        if (!AuthService.isAppUser(token)) {
          throw new Error("User is not a Myran user.");
        }

        const tokens = {
          access_token: token,
          refresh_token: result.user.refreshToken,
        };
        return tokens;
      })
      .catch(err => {
        throw err;
      });
  },

  isAppUser: (token: string | undefined) => {
    const tokenUser = AuthService.getUserFromAccessToken(token);
    return tokenUser && tokenUser.role && tokenUser.role.startsWith("Myran.") && tokenUser.organizationId;
  },

  getUserFromAccessToken: (accessToken: string | undefined) => {
    const tokeninfo = jwtDecode(accessToken || "") as any;
    if (tokeninfo) {
      const user: User = {
        name: tokeninfo.name,
        id: tokeninfo.sub,
        email: tokeninfo.email,
        role: tokeninfo.role,
        organizationId: tokeninfo.organizationId,
        preschoolIds: tokeninfo.preschoolIds,
      };
      return user;
    }
    return null;
  },

  sendPasswordResetMail: (x: { email: string; message: string }) => axiosAPIInstance.post(`/forgot-password`, x),

  storeTokens: ({ access_token, refresh_token }: { access_token: string; refresh_token: string }) => {
    cookies.set(token_key, access_token);
    cookies.set(refresh_token_key, refresh_token);
  },

  getToken: () => cookies.get(token_key),

  getRefreshToken: () => cookies.get(refresh_token_key),

  refreshToken: () =>
    client(axios.create(), {
      url: tokenUrl,
      grant_type: "refresh_token",
      client_id: clientId,
      refresh_token: AuthService.getRefreshToken(),
    })()
      .then((data: { access_token: string; refresh_token: string }) => {
        AuthService.storeTokens(data);
        return data;
      })
      .catch(() => {
        cookies.remove(token_key);
        cookies.remove(refresh_token_key);
        cookies.remove(user_key);
        window.location.reload();
      }),

  activateUser: async (inviteCode: string, type: string, token: string) => {
    const basicAuth = "Basic " + btoa(`:${inviteCode}`);
    const user = AuthService.getUserFromAccessToken(token);
    const axiosInstance = getAxiosInstance();
    const result = await axiosInstance.post(
      `/users/activate/` + inviteCode,
      { ...user, type },
      {
        headers: { Authorization: basicAuth },
      },
    );
    return result.data;
  },
  clearCookie: () => {
    cookies.remove(token_key);
    cookies.remove(refresh_token_key);
    cookies.remove(user_key);
  },
  getUser: () => {
    const tokeninfo = jwtDecode(AuthService.getToken() || "") as any;
    if (tokeninfo) {
      const user: User = {
        name: tokeninfo.name,
        id: tokeninfo.sub,
        email: tokeninfo.email,
        role: tokeninfo.role,
        preschool: "",
        organizationId: tokeninfo.organizationId,
        preschoolIds: tokeninfo.preschoolIds,
        username: tokeninfo.username,
      };
      return user;
    }
    return null;
  },
  cacheUser: user => {
    // Convert the user object to a JSON string
    const userJSON = JSON.stringify(user);

    // Set the cookie
    cookies.set(user_key, userJSON);
  },

  getCachedUser: () => JSON.parse(cookies.get(user_key) || "{}") as User,
};

export { AuthService };
